import * as SelectPrimitive from "@radix-ui/react-select";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import type { ComponentProps } from "react";

import { cn } from "../../lib/utils";
import { useSurfaceTheme, useSurfaceThemeClasses } from "./surface-theme";
import { useDialogContainer } from "./dialog";
import { inputClasses } from "./input";

export function Select({ ...props }: ComponentProps<typeof SelectPrimitive.Root>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />;
}

export function SelectGroup({ ...props }: ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

export function SelectValue({ ...props }: ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

export function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: ComponentProps<typeof SelectPrimitive.Trigger> & { size?: "sm" | "default" }) {
  return (
    <SelectPrimitive.Trigger
      aria-haspopup="listbox"
      className={cn(
        inputClasses,
        "flex cursor-pointer items-center justify-between gap-2 whitespace-nowrap data-placeholder:text-muted-foreground [&>span]:min-w-0 [&>span]:truncate",
        size === "default" ? "h-9 min-h-9 py-2 text-sm" : "h-8 min-h-8 px-2.5 text-xs",
        className,
      )}
      data-size={size}
      data-slot="select-trigger"
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

export function SelectContent({
  className,
  children,
  position = "popper",
  sideOffset,
  collisionPadding,
  align = "start",
  container,
  ...props
}: ComponentProps<typeof SelectPrimitive.Content> & { container?: HTMLElement | null }) {
  const dialogContainer = useDialogContainer();
  const showcase = useSurfaceTheme() !== "default";
  const theme = useSurfaceThemeClasses();
  return (
    <SelectPrimitive.Portal container={container ?? dialogContainer}>
      <SelectPrimitive.Content
        align={align}
        className={cn(
          "relative z-[80] max-h-[min(20rem,var(--radix-select-content-available-height))] min-w-36 origin-[var(--radix-select-content-transform-origin)] overflow-x-hidden overflow-y-auto rounded-lg border border-border bg-background text-foreground shadow-lg",
          showcase && "bg-popover text-popover-foreground shadow-md backdrop-blur-xl",
          position === "popper" && "w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-24px)]",
          position === "popper" && !showcase && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          theme,
          className,
        )}
        data-slot="select-content"
        position={position}
        sideOffset={sideOffset ?? (showcase ? 8 : undefined)}
        collisionPadding={collisionPadding ?? (showcase ? 12 : undefined)}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn("p-1", position === "popper" && "w-full scroll-my-1")}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

export function SelectLabel({ className, ...props }: ComponentProps<typeof SelectPrimitive.Label>) {
  return <SelectPrimitive.Label className={cn("px-2 py-1.5 text-xs text-muted-foreground", className)} data-slot="select-label" {...props} />;
}

export function SelectItem({ className, children, ...props }: ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      className={cn(
        "relative flex min-h-8 w-full cursor-pointer items-center gap-2 rounded-md py-1.5 pr-8 pl-2 text-sm outline-none select-none focus:bg-accent focus:text-accent-foreground data-disabled:cursor-not-allowed data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className,
      )}
      data-slot="select-item"
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4 text-foreground" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

export function SelectSeparator({ className, ...props }: ComponentProps<typeof SelectPrimitive.Separator>) {
  return <SelectPrimitive.Separator className={cn("-mx-1 my-1 h-px bg-border", className)} data-slot="select-separator" {...props} />;
}

export function SelectScrollUpButton({ className, ...props }: ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton className={cn("flex cursor-pointer items-center justify-center py-1", className)} data-slot="select-scroll-up-button" {...props}>
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

export function SelectScrollDownButton({ className, ...props }: ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton className={cn("flex cursor-pointer items-center justify-center py-1", className)} data-slot="select-scroll-down-button" {...props}>
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}
